
import { memo, useEffect, useState, useCallback } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from "@/integrations/supabase/client";

interface RoleNodeProps {
  data: {
    label: string;
    type: 'director' | 'manager' | 'team-member';
    node_label_changed?: boolean;
    is_editable?: boolean;
  };
  selected: boolean;
  id: string;
}

const RoleNode = ({ data, selected, id }: RoleNodeProps) => {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const [structureType, setStructureType] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [label, setLabel] = useState(data.label);
  const { setNodes } = useReactFlow();

  useEffect(() => {
    const fetchNodeLabel = async () => {
      if (!projectId || !data.node_label_changed) return;

      try {
        const { data: nodeData, error } = await supabase
          .from('nodes')
          .select('label')
          .eq('project_id', projectId)
          .eq('node_id', id)
          .maybeSingle();

        if (error) {
          console.error('Error fetching node label:', error);
          return;
        }

        if (nodeData?.label) {
          setLabel(nodeData.label);
        }
      } catch (error) {
        console.error('Error in fetchNodeLabel:', error);
      }
    };

    fetchNodeLabel();
  }, [projectId, id, data.node_label_changed]);

  const getBackgroundColor = () => {
    switch (data.type) {
      case 'director':
        return 'bg-secondary-light';
      case 'manager':
        return 'bg-primary/80';
      case 'team-member':
        return 'bg-[#1A1F2C]';
      default:
        return 'bg-primary';
    }
  };

  const getTextColor = () => {
    switch (data.type) {
      case 'director':
      case 'manager':
        return 'text-secondary';
      case 'team-member':
        return 'text-white';
      default:
        return 'text-secondary';
    }
  };

  const getBorderStyle = () => {
    if (selected) {
      return 'border-4 border-red-500';
    }
    
    switch (data.type) {
      case 'director':
        return 'border-2 border-primary';
      case 'manager':
        return 'border-2 border-secondary';
      case 'team-member':
        return 'border border-white';
      default:
        return '';
    }
  };

  const handleDoubleClick = useCallback(() => {
    // Only allow editing if the node is editable
    if (data.is_editable !== false) {
      setIsEditing(true);
    }
  }, [data.is_editable]);

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      setNodes(nodes => 
        nodes.map(node => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                label: label,
                node_label_changed: true
              },
              selected: false
            };
          }
          return node;
        })
      );
      
      if (projectId) {
        try {
          const { error: nodeError } = await supabase
            .from('nodes')
            .update({ 
              label,
              node_label_changed: true
            })
            .eq('project_id', projectId)
            .eq('node_id', id);

          if (nodeError) {
            console.error('Error updating node label:', nodeError);
          }
        } catch (error) {
          console.error('Error in updating node label:', error);
        }
      }
      
      setIsEditing(false);
      
      document.body.click();
    }
  };

  return (
    <div 
      className={`${getBackgroundColor()} ${getTextColor()} ${getBorderStyle()} rounded-lg px-4 py-2 shadow-md min-w-[150px] text-center`}
      onDoubleClick={handleDoubleClick}
    >
      <Handle 
        type="target" 
        position={Position.Top} 
        id="top"
        style={{ 
          width: '12px', 
          height: '12px',
          background: '#ea384c',
          border: 'none'
        }} 
      />
      <Handle 
        type="target" 
        position={Position.Left} 
        id="left"
        style={{ 
          width: '12px', 
          height: '12px',
          background: '#ea384c',
          border: 'none'
        }} 
      />
      {isEditing ? (
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={handleKeyDown}
          className="bg-transparent text-center w-full outline-none"
          autoFocus
        />
      ) : (
        label
      )}
      <Handle 
        type="source" 
        position={Position.Right} 
        id="right"
        style={{ 
          width: '12px', 
          height: '12px',
          background: '#ea384c',
          border: 'none'
        }} 
      />
      <Handle 
        type="source" 
        position={Position.Bottom} 
        id="bottom"
        style={{ 
          width: '12px', 
          height: '12px',
          background: '#ea384c',
          border: 'none'
        }} 
      />
    </div>
  );
};

export default memo(RoleNode);
